import { clampEditorGridColumns, editorGridDefaultColumns } from './landingEditorGeometry.js';
import type { LandingEditorElementFrame } from './landingEditorTypes.js';

/**
 * Repairs mask geometry created before static hero layout and copy wrappers were represented in the
 * editor registry. The image dimensions and focus stay intact; only invalid parent relationships move.
 */
export const repairStaticHeroLayoutFrames = (frames: Record<string, LandingEditorElementFrame>) => {
  const staticHeroImagePattern = /^(static\.(?:unternehmen|ablauf|ueber-vivaporta)\.hero)\.heroImage$/;
  for (const [imageId, imageFrame] of Object.entries(frames)) {
    const heroId = staticHeroImagePattern.exec(imageId)?.[1];
    if (!heroId) continue;
    const layoutId = `${heroId}.layout`;
    const copyId = `${heroId}.copy`;
    const heroFrame = { ...(frames[heroId] ?? { x: 0, y: 0 }) };
    const layoutFrame = { ...(frames[layoutId] ?? { x: 0, y: 0 }) };
    const copyFrame = { ...(frames[copyId] ?? { x: 0, y: 0 }) };
    let repaired = false;

    const repairViewport = (viewport: 'Desktop' | 'Mobile') => {
      const gridKey = `grid${viewport}` as const;
      const columnsKey = `gridColumns${viewport}` as const;
      const containerKey = `gridContainer${viewport}` as const;
      const placement = imageFrame[gridKey];
      if (placement?.parentId !== heroId) return;
      const columns = clampEditorGridColumns(
        imageFrame[columnsKey] ?? heroFrame[columnsKey] ?? editorGridDefaultColumns,
      );
      const defaultCopySpan = Math.max(1, Math.round(columns * 0.58));
      const imageStart = placement.columnStart > 1 ? placement.columnStart : Math.min(columns, defaultCopySpan + 1);

      imageFrame[gridKey] = {
        ...placement,
        columnStart: imageStart,
        columnSpan: Math.min(columns - imageStart + 1, placement.columnSpan),
        parentId: layoutId,
      };
      layoutFrame[containerKey] = true;
      layoutFrame[columnsKey] = columns;
      copyFrame[columnsKey] = columns;
      copyFrame[gridKey] = {
        columnStart: 1,
        columnSpan: Math.max(1, imageStart - 1),
        order: Math.max(0, placement.order - 1),
        parentId: layoutId,
      };
      delete heroFrame[containerKey];
      delete heroFrame[columnsKey];
      repaired = true;
    };

    repairViewport('Desktop');
    repairViewport('Mobile');
    if (!repaired) continue;
    frames[imageId] = imageFrame;
    frames[heroId] = heroFrame;
    frames[layoutId] = layoutFrame;
    frames[copyId] = copyFrame;
    for (const suffix of ['eyebrow', 'title', 'titleHighlight', 'intro']) {
      const textId = `${heroId}.${suffix}`;
      const textFrame = frames[textId];
      if (!textFrame) continue;
      const nextTextFrame = { ...textFrame };
      if (nextTextFrame.gridDesktop?.parentId === heroId) delete nextTextFrame.gridDesktop;
      if (nextTextFrame.gridMobile?.parentId === heroId) delete nextTextFrame.gridMobile;
      frames[textId] = nextTextFrame;
    }
  }
  return frames;
};
